import * as React from 'react';

import { fingerprint, newIdempotencyKey } from '../lib/idempotency';
import { readSession, removeSession, writeSession } from '../lib/session-store';

interface StoredKey {
  key: string;
  /** `fingerprint(key, body)` — never the body itself. */
  print: string;
}

/**
 * The `requestId` for a form that submits to an idempotent endpoint —
 * registrations, proposals, booking requests.
 *
 * The server treats one key as one submission: the same key with the same
 * body returns the original receipt instead of filing it twice, and the same
 * key with a different body is refused (`…_REQUEST_ID_REUSED`). So:
 *
 * - `keyFor(body)` reuses the last key while the body is unchanged — a retry
 *   after a lost response or a double click is safe — and makes a new one as
 *   soon as anything in the body differs;
 * - `reset()` throws the key away: call it once the server has answered with
 *   a receipt, and when it has refused the key.
 *
 * The key outlives a reload. It is written to `sessionStorage` under
 * `storageName` *before* the request goes out, so a visitor whose response
 * was lost, who reloads and sends the same details again, gets their original
 * receipt rather than a second registration. Only the key and a fingerprint
 * of the body are stored — never the body. Where storage is unavailable it
 * falls back to memory, which still covers retries within the page.
 *
 * `storageName` should be one per form and target, e.g.
 * `idem:event-registration:<eventId>`.
 */
export function useIdempotencyKey(storageName: string) {
  const memory = React.useRef<StoredKey | null>(null);

  const keyFor = React.useCallback(
    (body: unknown): string => {
      const stored = memory.current ?? readSession<StoredKey>(storageName);
      if (stored && stored.print === fingerprint(stored.key, body)) {
        memory.current = stored;
        return stored.key;
      }
      const key = newIdempotencyKey();
      memory.current = { key, print: fingerprint(key, body) };
      writeSession(storageName, memory.current);
      return key;
    },
    [storageName],
  );

  const reset = React.useCallback(() => {
    memory.current = null;
    removeSession(storageName);
  }, [storageName]);

  return { keyFor, reset };
}
